import { cn } from '@paulownia/shared/lib/utils';
import type { DayOfWeek } from '@paulownia/shared/types';

/**
 * Short day labels for compact display
 */
const DAY_SHORT_LABELS: Record<DayOfWeek, string> = {
  1: 'Lun',
  2: 'Mar',
  3: 'Mer',
  4: 'Jeu',
  5: 'Ven',
  6: 'Sam',
  7: 'Dim',
};

interface DaySelectorProps {
  selectedDay: DayOfWeek;
  onDayChange: (day: DayOfWeek) => void;
  /**
   * Count of selected markets per day of week.
   * Key is DayOfWeek (1-7), value is count.
   */
  marketCountsByDay: Record<DayOfWeek, number>;
  className?: string;
}

/**
 * Day of week selector with badges showing selected market counts.
 * Used in the market selection step of onboarding.
 */
export function DaySelector({
  selectedDay,
  onDayChange,
  marketCountsByDay,
  className,
}: DaySelectorProps) {
  const days: DayOfWeek[] = [1, 2, 3, 4, 5, 6, 7];

  return (
    <div className={cn('flex gap-1', className)}>
      {days.map((day) => {
        const count = marketCountsByDay[day] || 0;
        const isSelected = day === selectedDay;

        return (
          <button
            key={day}
            type="button"
            onClick={() => onDayChange(day)}
            className={cn(
              'relative flex-1 py-2 px-1 text-sm font-medium rounded-lg transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1',
              isSelected
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {DAY_SHORT_LABELS[day]}
            {count > 0 && (
              <span
                className={cn(
                  'absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1',
                  'text-xs font-bold rounded-full flex items-center justify-center',
                  isSelected
                    ? 'bg-white text-orange-600'
                    : 'bg-orange-600 text-white'
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
